/*
 * Operator-drafted legal page templates prepared by Plateau Nerd — not legal
 * advice. Flag for review by the site owner's attorney before relying on them.
 */
import { useEffect } from 'react';
import { openCookieSettings } from '../services/consent.js';
import { SITE } from './siteConfig.js';

export const LEGAL_ROUTES = {
  '/accessibility': { title: 'Accessibility Statement', Component: AccessibilityStatement },
  '/privacy': { title: 'Privacy Policy', Component: PrivacyPolicy },
  '/cookies': { title: 'Cookie Policy', Component: CookiePolicy },
  '/terms': { title: 'Terms of Use', Component: TermsOfUse }
};

export function LegalFooterLinks({ className = 'legal-links' }) {
  return (
    <nav className={className} aria-label="Legal">
      <a href="/accessibility">Accessibility</a>
      <a href="/privacy">Privacy</a>
      <a href="/cookies">Cookies</a>
      <a href="/terms">Terms</a>
      <button type="button" className="legal-links__btn" onClick={openCookieSettings}>
        Cookie Settings
      </button>
      <button type="button" className="legal-links__btn" onClick={openCookieSettings}>
        Do Not Sell or Share My Personal Information
      </button>
    </nav>
  );
}

export function LegalPage({ path }) {
  const route = LEGAL_ROUTES[path];
  useEffect(() => {
    document.title = `${route.title} — Eliza`;
    return () => {
      document.title = 'Eliza';
    };
  }, [route.title]);

  const { Component } = route;
  return (
    <div className="legal-page">
      <a className="skip-link" href="#main-content">
        Skip to main content
      </a>
      <header className="legal-page__header">
        <a href="/" className="legal-page__home">
          ← Eliza
        </a>
      </header>
      <main id="main-content" className="legal-page__body">
        <h1>{route.title}</h1>
        <p className="legal-page__effective">Effective date: {SITE.EFFECTIVE_DATE}</p>
        <Component />
      </main>
      <footer className="legal-page__footer">
        <LegalFooterLinks />
      </footer>
    </div>
  );
}

function AccessibilityStatement() {
  return (
    <>
      <p>
        {SITE.LEGAL_NAME} is committed to making {SITE.DOMAIN} accessible to everyone,
        including people who use assistive technology. Our goal is conformance with the
        Web Content Accessibility Guidelines (WCAG) 2.1, Level AA.
      </p>
      <h2>Measures we take</h2>
      <p>
        The site is built with semantic HTML landmarks and headings, full keyboard
        operability with visible focus indicators, text alternatives for non-text
        content, color contrast meeting or exceeding WCAG 2.1 AA ratios, labels on all
        form controls, support for the browser&rsquo;s reduced-motion preference, and
        layouts that remain usable at 200% zoom and on small screens. We test with
        automated tools (axe, Lighthouse) and manual keyboard passes as part of each
        release.
      </p>
      <h2>Ongoing effort</h2>
      <p>
        Accessibility is an ongoing effort, not a one-time fix. Some parts of the site
        may not yet meet every guideline, and we review and remediate issues as they are
        found.
      </p>
      <h2>Report a barrier</h2>
      <p>
        If you encounter anything on this site that is difficult or impossible to use,
        please tell us at <a href={`mailto:${SITE.CONTACT_EMAIL}`}>{SITE.CONTACT_EMAIL}</a>.
        Include the page and a short description of the problem. We will respond within
        a few business days and work to fix confirmed barriers promptly.
      </p>
    </>
  );
}

function PrivacyPolicy() {
  return (
    <>
      <p>
        This policy describes how {SITE.LEGAL_NAME} (&ldquo;we,&rdquo; &ldquo;us&rdquo;)
        handles personal information on {SITE.DOMAIN}. {SITE.LEGAL_NAME} is the business
        responsible for this site. Eliza is a private, invitation-only personal
        wellness application — it is not open for public registration, and it shows no
        advertising and runs no analytics or tracking scripts.
      </p>
      <h2>What we collect</h2>
      <p>
        <strong>Account information.</strong> When you sign in with Google, we receive
        your Google account email address and basic profile identity through AWS
        Cognito, which manages authentication.
      </p>
      <p>
        <strong>Content you enter.</strong> Conversations with the Eliza assistant,
        mood check-ins, goals, habits, notes, and preferences you save in the app.
      </p>
      <p>
        <strong>Notifications.</strong> If you enable check-in reminders, your
        browser&rsquo;s push subscription endpoint is stored so reminders can be
        delivered.
      </p>
      <p>
        <strong>Technical logs.</strong> Standard server logs (including IP address and
        request metadata) are generated by our hosting infrastructure for security and
        troubleshooting.
      </p>
      <h2>How we use it</h2>
      <p>
        Solely to provide the application: authenticating you, storing and displaying
        your data back to you, generating assistant responses and weekly insights, and
        sending the reminders you asked for. We do not sell or share personal
        information, use it for advertising, or use third-party analytics.
      </p>
      <h2>Service providers</h2>
      <p>
        <strong>Amazon Web Services</strong> hosts the site and stores data (US East
        region). <strong>Anthropic</strong> processes conversation content and related
        context to generate assistant responses via its API; under Anthropic&rsquo;s
        commercial API terms this content is not used to train its models.{' '}
        <strong>Google</strong> provides sign-in. Each acts as a processor on our
        behalf; none receives data for its own advertising purposes.
      </p>
      <h2>Retention and deletion</h2>
      <p>
        Your content is retained while your account is active so the app can work.
        You can delete individual items (goals, habits, moods) in the app at any time.
        To request access to, or deletion of, everything we hold about you, email{' '}
        <a href={`mailto:${SITE.CONTACT_EMAIL}`}>{SITE.CONTACT_EMAIL}</a> — we will
        respond to verified requests.
      </p>
      <h2>Your choices</h2>
      <p>
        This site honors the Global Privacy Control (GPC) browser signal. Because no
        analytics or marketing trackers run here, there is nothing to opt out of today;
        a GPC signal is still recorded as an opt-out. Cookie preferences can be reviewed
        any time via the &ldquo;Cookie Settings&rdquo; link in the footer.
      </p>
      <h2>Contact</h2>
      <p>
        Questions about this policy: <a href={`mailto:${SITE.CONTACT_EMAIL}`}>{SITE.CONTACT_EMAIL}</a>.
      </p>
    </>
  );
}

function CookiePolicy() {
  return (
    <>
      <p>
        This page lists every cookie and browser-storage item {SITE.DOMAIN} uses. All of
        it is strictly necessary — the site sets no analytics, advertising, or other
        third-party tracking cookies, and no third-party tracking scripts load at any
        time. Manage preferences via{' '}
        <button type="button" className="legal-inline-btn" onClick={openCookieSettings}>
          Cookie Settings
        </button>
        .
      </p>
      <div className="legal-table-wrap">
        <table className="legal-table">
          <caption className="sr-only">Cookies and storage used by this site</caption>
          <thead>
            <tr>
              <th scope="col">Item</th>
              <th scope="col">Category</th>
              <th scope="col">Purpose</th>
              <th scope="col">Duration</th>
              <th scope="col">Provider</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Cognito auth tokens (localStorage)</td>
              <td>Strictly necessary</td>
              <td>Keeps you signed in securely</td>
              <td>Until sign-out / token expiry</td>
              <td>AWS Cognito (first party)</td>
            </tr>
            <tr>
              <td>pn_consent (localStorage)</td>
              <td>Strictly necessary</td>
              <td>Remembers your cookie preferences</td>
              <td>12 months</td>
              <td>First party</td>
            </tr>
            <tr>
              <td>eliza-dashboard (localStorage)</td>
              <td>Strictly necessary</td>
              <td>Remembers whether the dashboard panel is open</td>
              <td>Persistent</td>
              <td>First party</td>
            </tr>
            <tr>
              <td>Offline queue (IndexedDB)</td>
              <td>Strictly necessary</td>
              <td>Saves your entries while offline and syncs them later</td>
              <td>Until synced</td>
              <td>First party</td>
            </tr>
            <tr>
              <td>App cache (service worker)</td>
              <td>Strictly necessary</td>
              <td>Lets the app load offline</td>
              <td>Until updated</td>
              <td>First party</td>
            </tr>
            <tr>
              <td>Push subscription</td>
              <td>Strictly necessary</td>
              <td>Delivers check-in reminders you enabled</td>
              <td>Until you disable reminders</td>
              <td>Your browser&rsquo;s push service</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p>
        Analytics and Marketing categories exist in Cookie Settings for transparency,
        but no items in those categories are used on this site.
      </p>
    </>
  );
}

function TermsOfUse() {
  return (
    <>
      <h2>Ownership and responsibility</h2>
      <p>
        This website and all content, services, and functionality offered through it are
        owned and operated by {SITE.LEGAL_NAME}, which is solely responsible for the
        content and services presented on this site, and which also provides the
        site&rsquo;s design, hosting, and technical administration.
      </p>
      <h2>Not professional advice</h2>
      <p>
        Eliza is a personal wellness and accountability tool. Conversations, exercises,
        and insights it produces are general wellness information generated by an AI
        assistant — they are not medical care, psychotherapy, diagnosis, or treatment,
        and they are not a substitute for advice from a physician or licensed mental
        health professional. If you are in crisis or thinking about harming yourself,
        call or text 988 (Suicide &amp; Crisis Lifeline) or dial 911.
      </p>
      <h2>Access and acceptable use</h2>
      <p>
        Access is by invitation only. You agree to use the site only for its intended
        personal purpose and not to attempt to gain unauthorized access to accounts,
        systems, or data, interfere with the operation of the service, or use it for any
        unlawful purpose.
      </p>
      <h2>Your content and intellectual property</h2>
      <p>
        You retain ownership of the content you enter. The application&rsquo;s software,
        design, and branding are the property of their respective owners and may not be
        copied or reused without permission.
      </p>
      <h2>Disclaimer of warranties</h2>
      <p>
        The site is provided &ldquo;as is&rdquo; and &ldquo;as available,&rdquo; without
        warranties of any kind, express or implied, including fitness for a particular
        purpose and non-infringement. AI-generated responses may be inaccurate or
        incomplete.
      </p>
      <h2>Limitation of liability</h2>
      <p>
        To the fullest extent permitted by law, {SITE.LEGAL_NAME} shall not be liable
        for any indirect, incidental, special, or consequential damages arising from
        your use of, or inability to use, this site.
      </p>
      <h2>Governing law</h2>
      <p>
        These terms are governed by the laws of the State of California, without regard
        to conflict-of-law principles.
      </p>
      <h2>Contact</h2>
      <p>
        Questions about these terms: <a href={`mailto:${SITE.CONTACT_EMAIL}`}>{SITE.CONTACT_EMAIL}</a>.
      </p>
    </>
  );
}
